/* eslint-disable complexity */
import React, { useContext, useEffect } from 'react';
import './ProductVoucher.css';
import VendorCard from '../Vendor/Card/VendorCard.jsx';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import PropTypeShapes from '../../services/Constants/PropTypeShapes.jsx';
import PropTypes from 'prop-types';
import { Voucher } from '../../services/Constants/ObjectDefinitions.js';
import { getVendorCategory, getVoucherVendor } from '../../services/ReverseHierarchySearch.js';
import { CategoriesContext } from '../../services/context/CategoriesContext.js';
import { SubscriptionDetailsContext } from '../../services/context/SubscriptionDetailsContext.js';
import { LimitsContext } from '../../services/context/LimitsContext.js';
import { getVoucherLimitInfo } from '../../services/utils/vendorLimits.js';
import VodaPayClubIcon from '../../assets/VodaPayClubIcon.jsx';
import { defaultAnalyticsVariables, events, pagePrefix, hotDeals } from '../../services/Constants/Analytics.js';
import useNormalizedSubscriptionStatus from '../../services/utils/subscriptionUtils.js';
import { homePageSliderCartEvent } from '../../services/Constants/config.js';
import { EligibilityContext } from '../../services/context/EligibilityContext.js';
import { createIsAdvanceOptionFilter } from '../../services/EligibilityOffers.js';
import { CartContext } from '../../services/context/CartContext.js';

/**
 *
 * @param {object} props - the props of the component
 * @param {Voucher} props.voucher - the voucher to display
 * @param {JSX.Element} props.actionItem - the action item element to display
 * @param {string} props.descriptionType - which description to display
 * @returns {JSX.Element} ProductVoucher Component
 * @param {JSX.Element | string} props.addedValue - Element for added value
 * @param {string} props.pageLabel - which description to display
 * @param {JSX.Element} props.isClubVoucher - Prop to check ClubVoucher category
 * @param props.stock
 * @param props.remaining
 */
export default function ProductVoucher({
  voucher, actionItem, descriptionType, addedValue,
  isClubVoucher, pageLabel, stock, remaining,
}) {
  const categories = useContext(CategoriesContext);
  const MoneyFormatter = useContext(MoneyFormatterContext);
  const skeletonLoader = 'skeleton-loader';
  const isEmpty = !voucher;
  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const isSubscribed = subscriptionDetails?.isSubscribed;
  const getClass = base => (!isSubscribed && isClubVoucher ? `${base} v-disable` : base);
  const subscriptionStatus = useNormalizedSubscriptionStatus();
  const pageNameForAnalytics = pageLabel === homePageSliderCartEvent ? `${pagePrefix}: landing` : `${pagePrefix}: hot deals`;
  const linkIdForAnalytics = pageLabel === homePageSliderCartEvent ? `${pagePrefix}: hot deals for you: ${voucher?.vendorName}` : `${pagePrefix}: hot deals: view deal: ${voucher?.vendorName}`;
  let productPackageName = '';
  let productPrice = '';
  if (voucher) {
    productPackageName = `Pay ${MoneyFormatter.format(MoneyFormatter.getValue(voucher?.cost))}, get ${MoneyFormatter.format(MoneyFormatter.getValue(voucher?.options?.buyNow?.value))}`;
    productPrice = MoneyFormatter.getValue(voucher?.cost).amount;
  }

  const cart = useContext(CartContext);
  const eligibility = useContext(EligibilityContext);
  const limits = useContext(LimitsContext);

  // Check voucher is advance voucher or not and pass the info to getVoucherLimitInfo
  const isAdvance = voucherObj => {
    const isAdvanceOption = createIsAdvanceOptionFilter(
      eligibility,
      voucherObj,
      MoneyFormatter,
    );
    return Boolean(voucherObj?.options?.advance.terms.options?.some(option => isAdvanceOption(option)));
  };

  const {
    stock: actualStock,
    used: actualUsed,
    isFullyReserved,
    hasLimitData: rawHasLimitData,
  } = getVoucherLimitInfo(limits, voucher?.id, cart, {
    fallbackStock: stock,
    fallbackRemaining: remaining,
  }, isAdvance(voucher));

  // const hasLimitData = !disableLimits && rawHasLimitData;
  const remainingCount = actualStock - actualUsed;

  useEffect(() => {
    if (voucher?.campaign) {
      window.utag?.view({
        ...defaultAnalyticsVariables,
        event_name: [
          events.interaction,
          events.pageView,
        ],
        page_name: pageNameForAnalytics,
        link_id: linkIdForAnalytics,
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
        product_name: voucher?.name,
        product_price: productPrice,
        product_package_name: productPackageName,
        product_category: getVendorCategory(categories, getVoucherVendor(categories, voucher))?.name,
        product_variant: hotDeals,
      });
    }
  }, []);

  const getVodapayIconAndInfoHtml = () => (
    <div className={ `action-item ${isClubVoucher ? 'club-action-style' : ''}` }>
      {
        isClubVoucher && addedValue
          ? <div className={ getClass('added-value-and-icon') }>
            {addedValue}
            <VodaPayClubIcon />
          </div >
          : isClubVoucher
        && <div className={ getClass('added-value-and-icon') }>
          <VodaPayClubIcon />
        </div>
      }
      { actionItem }
    </div>
  );

  return (
    <div className={ `product-voucher v-container${isEmpty ? ' empty' : ''}` }>
      <section>
        <VendorCard
          vendor={
            isEmpty ? null : {
              backgroundUrl: voucher.productUrl,
              iconUrl: getVoucherVendor(categories, voucher)?.iconUrl,
            }
          }
          hideInfo={ true }
        />
      </section>
      <section className='info v-container'>
        <header className={ getClass('container-v') }>
          <h3 className={ isEmpty ? skeletonLoader : undefined }>
            {isEmpty ? null : voucher.name }
          </h3>
          <h3 className={ isEmpty ? skeletonLoader : undefined }>
            {isEmpty ? null : MoneyFormatter.format(MoneyFormatter.getValue(voucher.cost))}
          </h3>
        </header>
        <p className={ `${getClass('')} ${isEmpty ? skeletonLoader : ''}` } >
          {isEmpty ? null : voucher.options[descriptionType].description}
        </p>
        {
          rawHasLimitData && (
            <div
              className={ `stock-rate-limit ${isFullyReserved ? 'reserved' : ''}` }
            >
              {
                isFullyReserved
                  ? `${actualStock} of ${actualStock} reserved`
                  : `${remainingCount} of ${actualStock} left`
              }
            </div>
          )
        }
        {
          actionItem
          && getVodapayIconAndInfoHtml()
        }
      </section>
    </div>
  );
}

ProductVoucher.propTypes = {
  voucher: PropTypeShapes.voucher,
  actionItem: PropTypes.element,
  descriptionType: PropTypes.oneOf([
    'advance',
    'buyNow',
  ]),
  isClubVoucher: PropTypes.bool,
  addedValue: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
  ]),
  stock: PropTypes.number,
  remaining: PropTypes.number,
  iconUrl: PropTypes.string,
  pageLabel: PropTypes.string,
};
